import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Smartphone,
  Battery,
  BatteryCharging,
  HardDrive,
  Wifi,
  MapPin,
  Clock,
  RefreshCw,
  Send,
} from "lucide-react";
import { Button, Skeleton } from "@finora/ui";
import type { DeviceCommand } from "@finora/types";
import { api } from "../../lib/api";

/**
 * Detalj jednog uredjaja (Finora Mobile) - "Uredjaji" admin ekran, vidi
 * claude/uredjaji-admin-i-flutter-telemetrija-todo.md i
 * claude/uredjaji-flutter-telemetrija-kontrakt.md (24.09.2026). Backend
 * (heartbeat/command-ack/detalj/aktivnost/komande rute) je vec gotov i
 * potvrdjen uzivo - ovaj ekran je frontend koji ga konzumira. Dijagnostika
 * ostaje prazna dok Flutter tim ne implementira slanje heartbeat-a - ekran
 * i dalje ima vrednost odmah (osnovne informacije, moduli, komande, test
 * push, aktivnost registracije).
 */

const COMMAND_LABELS: Record<DeviceCommand, string> = {
  RESTART_SESSION: "Restartuj sesiju",
  REFRESH_LICENSE: "Osveži licencu",
  REFRESH_MODULES: "Osveži module",
  SYNC_PRODUCTS: "Sinhronizuj artikle",
  REQUEST_DIAGNOSTICS: "Pošalji dijagnostiku",
  LOCK_REGISTER: "Zaključaj kasu",
  LOGOUT_DEVICE: "Odjavi uređaj",
};

const LPFR_STATE_LABELS: Record<string, string> = {
  spreman: "L-PFR spreman",
  trebaPin: "L-PFR traži PIN",
  nemaKartice: "L-PFR ne vidi karticu u čitaču",
  nijeDostupan: "L-PFR nije dostupan",
  nijePodesen: "L-PFR nije podešen",
  blokiran: "Kartica je zaključana",
};

const DEVICE_COMMAND_LIST: DeviceCommand[] = [
  "RESTART_SESSION",
  "REFRESH_LICENSE",
  "REFRESH_MODULES",
  "SYNC_PRODUCTS",
  "REQUEST_DIAGNOSTICS",
  "LOCK_REGISTER",
  "LOGOUT_DEVICE",
];

const ACTIVITY_LABELS: Record<string, string> = {
  REGISTERED: "Registrovan",
  HEARTBEAT: "Heartbeat",
  COMMAND_SENT: "Komanda poslata",
  COMMAND_ACK: "Komanda potvrđena",
  ERROR: "Greška",
};

function isOnline(lastActiveAt: string): boolean {
  return Date.now() - new Date(lastActiveAt).getTime() < 5 * 60 * 1000;
}

function formatMb(mb: number | null): string {
  if (mb == null) return "—";
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb} MB`;
}

function formatUptime(seconds: number | null): string {
  if (seconds == null) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h}h ${m}min` : `${m}min`;
}

export function DeviceDetailPage() {
  const { companyId, deviceId } = useParams<{ companyId: string; deviceId: string }>();
  const queryClient = useQueryClient();
  const [pushTitle, setPushTitle] = useState("");
  const [pushBody, setPushBody] = useState("");

  const { data: device, isLoading } = useQuery({
    queryKey: ["device-detail", companyId, deviceId],
    queryFn: () => api.getDeviceDetail(companyId!, deviceId!),
    enabled: !!companyId && !!deviceId,
    refetchInterval: 30_000,
  });

  const { data: activity, isLoading: activityLoading } = useQuery({
    queryKey: ["device-activity", companyId, deviceId],
    queryFn: () => api.listDeviceActivity(companyId!, deviceId!),
    enabled: !!companyId && !!deviceId,
  });

  const { data: modules, isLoading: modulesLoading } = useQuery({
    queryKey: ["device-modules", companyId, deviceId],
    queryFn: () => api.listDeviceModules(companyId!, deviceId!),
    enabled: !!companyId && !!deviceId,
  });

  const toggleModule = useMutation({
    mutationFn: (input: { featureKey: string; allowed: boolean }) =>
      api.setDeviceModule(companyId!, deviceId!, input.featureKey, input.allowed),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["device-modules", companyId, deviceId] }),
  });

  const sendCommand = useMutation({
    mutationFn: (command: DeviceCommand) => api.sendDeviceCommand(companyId!, deviceId!, command),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["device-activity", companyId, deviceId] }),
  });

  const testPush = useMutation({
    mutationFn: () =>
      api.sendTestPushToDevice(
        companyId!,
        deviceId!,
        pushTitle || pushBody ? { title: pushTitle, body: pushBody } : undefined,
      ),
  });

  if (!companyId || !deviceId) return null;

  if (isLoading || !device) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const online = isOnline(device.lastActiveAt);
  const hasTelemetry = device.batteryPercent != null || device.freeStorageMb != null || device.networkType != null;

  return (
    <div className="space-y-4">
      <Link
        to={`/finora-admin/companies/${companyId}`}
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden /> Nazad na firmu
      </Link>

      <div className="flex items-center gap-3 rounded-xl border border-border bg-surface p-5 shadow-subtle">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-light text-brand">
          <Smartphone className="h-6 w-6" aria-hidden />
        </div>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-lg font-semibold text-slate-900">
              {device.deviceName ?? device.model ?? "Nepoznat uređaj"}
            </h1>
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                online ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
              }`}
            >
              {online ? "Online" : "Offline"}
            </span>
            {!device.isActive && (
              <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-600">
                Deaktiviran
              </span>
            )}
          </div>
          <p className="text-sm text-slate-500">
            {device.user.fullName} ({device.user.email}) · {device.company.name}
          </p>
          <p className="text-xs text-slate-400">
            Poslednja aktivnost: {new Date(device.lastActiveAt).toLocaleString("sr-RS")}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
          <h2 className="mb-3 text-sm font-semibold text-slate-800">Osnovne informacije</h2>
          <dl className="grid grid-cols-2 gap-y-2 text-sm">
            <dt className="text-slate-400">Platforma</dt>
            <dd className="text-slate-700">{device.platform}</dd>
            <dt className="text-slate-400">Model</dt>
            <dd className="text-slate-700">{device.model ?? "—"}</dd>
            <dt className="text-slate-400">OS verzija</dt>
            <dd className="text-slate-700">{device.osVersion ?? "—"}</dd>
            <dt className="text-slate-400">App verzija</dt>
            <dd className="text-slate-700">{device.appVersion ?? "—"}</dd>
            <dt className="text-slate-400">Jezik</dt>
            <dd className="text-slate-700">{device.language ?? "—"}</dd>
            <dt className="text-slate-400">Rezolucija</dt>
            <dd className="text-slate-700">
              {device.screenWidth && device.screenHeight ? `${device.screenWidth}×${device.screenHeight}` : "—"}
            </dd>
            <dt className="text-slate-400">Registrovan</dt>
            <dd className="text-slate-700">{new Date(device.createdAt).toLocaleDateString("sr-RS")}</dd>
          </dl>
        </div>

        <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
          <h2 className="mb-3 text-sm font-semibold text-slate-800">Dijagnostičke informacije</h2>
          {!hasTelemetry ? (
            <p className="text-sm text-slate-400">
              Nema još telemetrije sa ovog uređaja (Flutter aplikacija još ne šalje heartbeat).
            </p>
          ) : (
            <dl className="grid grid-cols-2 gap-y-2 text-sm">
              <dt className="flex items-center gap-1.5 text-slate-400">
                {device.isCharging ? (
                  <BatteryCharging className="h-3.5 w-3.5" aria-hidden />
                ) : (
                  <Battery className="h-3.5 w-3.5" aria-hidden />
                )}
                Baterija
              </dt>
              <dd className="text-slate-700">
                {device.batteryPercent != null ? `${device.batteryPercent}%` : "—"}
                {device.isCharging ? " (puni se)" : ""}
              </dd>
              <dt className="flex items-center gap-1.5 text-slate-400">
                <HardDrive className="h-3.5 w-3.5" aria-hidden /> Prostor
              </dt>
              <dd className="text-slate-700">
                {formatMb(device.freeStorageMb)} slobodno / {formatMb(device.totalStorageMb)} ukupno
              </dd>
              <dt className="text-slate-400">RAM</dt>
              <dd className="text-slate-700">
                {formatMb(device.freeRamMb)} slobodno / {formatMb(device.totalRamMb)} ukupno
              </dd>
              <dt className="flex items-center gap-1.5 text-slate-400">
                <Wifi className="h-3.5 w-3.5" aria-hidden /> Mreža
              </dt>
              <dd className="text-slate-700">
                {device.networkType ?? "—"}
                {device.wifiSsid ? ` (${device.wifiSsid})` : ""}
                {device.signalStrengthDbm != null ? ` · ${device.signalStrengthDbm} dBm` : ""}
              </dd>
              {(device.latitude != null || device.longitude != null) && (
                <>
                  <dt className="flex items-center gap-1.5 text-slate-400">
                    <MapPin className="h-3.5 w-3.5" aria-hidden /> Lokacija
                  </dt>
                  <dd className="text-slate-700">
                    {device.latitude?.toFixed(5)}, {device.longitude?.toFixed(5)}
                  </dd>
                </>
              )}
              <dt className="flex items-center gap-1.5 text-slate-400">
                <Clock className="h-3.5 w-3.5" aria-hidden /> Uptime app-a
              </dt>
              <dd className="text-slate-700">{formatUptime(device.uptimeSeconds)}</dd>
              <dt className="text-slate-400">Broj pokretanja</dt>
              <dd className="text-slate-700">{device.launchCount ?? "—"}</dd>
              <dt className="text-slate-400">Vreme na uređaju</dt>
              <dd className="text-slate-700">
                {device.deviceTimestamp ? new Date(device.deviceTimestamp).toLocaleString("sr-RS") : "—"}
              </dd>
            </dl>
          )}
        </div>
      </div>

      <div className="max-w-2xl rounded-xl border border-border bg-surface p-3 shadow-subtle">
        <h2 className="mb-2 text-sm font-semibold text-slate-800">L-PFR dijagnostika</h2>
        {!device.lpfrState ? (
          <p className="text-sm text-slate-400">
            Ovaj uređaj još nije poslao L-PFR stanje. Pošaljite komandu „Pošalji dijagnostiku“ ispod.
          </p>
        ) : (
          <div className="space-y-2 text-sm">
            <p className={device.lpfrState === "spreman" ? "font-semibold text-emerald-700" : "font-semibold text-red-700"}>
              {LPFR_STATE_LABELS[device.lpfrState] ?? device.lpfrState}
            </p>
            <p className="text-xs text-slate-500">
              Javna IP kase: <b className="text-slate-700">{device.lastIp ?? "—"}</b>
              {typeof device.lpfrInfo?.address === "string" && (
                <>
                  {" · "}Adresa L-PFR-a: <b className="text-slate-700">{device.lpfrInfo.address}</b>
                </>
              )}
            </p>
            {device.lpfrCheckedAt && (
              <p className="text-xs text-slate-400">Provereno {new Date(device.lpfrCheckedAt).toLocaleString("sr-RS")}</p>
            )}
            {device.lpfrInfo && Object.keys(device.lpfrInfo).length > 0 && (
              <dl className="grid grid-cols-[150px_1fr] gap-y-0.5 text-xs">
                {Object.entries(device.lpfrInfo).filter(([k]) => k !== "address").map(([k, v]) => (
                  <div key={k} className="contents">
                    <dt className="text-slate-400">{k}</dt>
                    <dd className="break-all text-slate-700">{typeof v === "object" ? JSON.stringify(v) : String(v)}</dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        )}
      </div>

      <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
        <h2 className="mb-3 text-sm font-semibold text-slate-800">Dodeljeni moduli (licenca po uređaju)</h2>
        {modulesLoading ? (
          <Skeleton className="h-10 w-full" />
        ) : !modules || modules.length === 0 ? (
          <p className="text-sm text-slate-400">
            Firma nema aktivan nijedan mobilni modul (kasa/računi/prijem robe), ili nema licence.
          </p>
        ) : (
          <div className="flex flex-wrap gap-4">
            {modules.map((m) => (
              <label key={m.featureKey} className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={m.allowed}
                  disabled={toggleModule.isPending}
                  onChange={(e) => toggleModule.mutate({ featureKey: m.featureKey, allowed: e.target.checked })}
                  className="h-4 w-4 rounded border-border text-brand focus:ring-brand"
                />
                {m.name}
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
        <h2 className="mb-1 text-sm font-semibold text-slate-800">Pošalji komandu</h2>
        <p className="mb-3 text-xs text-slate-400">
          Komanda se šalje kao FCM data poruka - stvaran efekat zavisi od toga da li Flutter aplikacija ima
          implementiran handler za nju (vidi kontrakt).
        </p>
        <div className="flex flex-wrap gap-2">
          {DEVICE_COMMAND_LIST.map((cmd) => (
            <Button
              key={cmd}
              size="sm"
              variant="secondary"
              disabled={sendCommand.isPending}
              onClick={() => sendCommand.mutate(cmd)}
            >
              <RefreshCw className="h-3.5 w-3.5" aria-hidden />
              {COMMAND_LABELS[cmd]}
            </Button>
          ))}
        </div>
        {sendCommand.isSuccess && (
          <p className="mt-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{sendCommand.data?.queued ? "Poslato — kasa na računaru je uzima pri sledećem javljanju (do 30 s)." : "Komanda poslata."}</p>
        )}
        {sendCommand.isError && (
          <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {sendCommand.error instanceof Error ? sendCommand.error.message : "Slanje nije uspelo."}
          </p>
        )}

        <div className="mt-4 border-t border-border pt-4">
          <p className="mb-2 text-xs font-medium text-slate-500">Test obaveštenje (obična vidljiva notifikacija)</p>
          <div className="grid grid-cols-2 gap-2">
            <input
              value={pushTitle}
              onChange={(e) => setPushTitle(e.target.value)}
              placeholder="Naslov"
              className="h-9 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
            />
            <input
              value={pushBody}
              onChange={(e) => setPushBody(e.target.value)}
              placeholder="Tekst poruke"
              className="h-9 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
            />
          </div>
          <Button
            size="sm"
            className="mt-2"
            disabled={!device.isActive || testPush.isPending}
            onClick={() => testPush.mutate()}
          >
            <Send className="h-3.5 w-3.5" aria-hidden /> Pošalji test obaveštenje
          </Button>
          {testPush.isSuccess && (
            <p className="mt-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">Push poslat.</p>
          )}
          {testPush.isError && (
            <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {testPush.error instanceof Error ? testPush.error.message : "Slanje nije uspelo."}
            </p>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-border bg-surface shadow-subtle">
        <div className="border-b border-border px-4 py-2">
          <h2 className="text-sm font-semibold text-slate-800">Aktivnost</h2>
        </div>
        {activityLoading ? (
          <div className="p-4">
            <Skeleton className="h-20 w-full" />
          </div>
        ) : !activity || activity.length === 0 ? (
          <p className="p-4 text-sm text-slate-400">Još nema evidentirane aktivnosti.</p>
        ) : (
          <div className="divide-y divide-border">
            {activity.map((a) => (
              <div key={a.id} className="flex items-center justify-between px-4 py-2 text-sm">
                <div>
                  <span className="font-medium text-slate-700">{ACTIVITY_LABELS[a.type] ?? a.type}</span>
                  {a.command && (
                    <span className="ml-2 text-xs text-slate-400">
                      ({COMMAND_LABELS[a.command as DeviceCommand] ?? a.command})
                    </span>
                  )}
                  {a.status && (
                    <span
                      className={`ml-2 text-xs font-medium ${
                        a.status === "DONE" ? "text-emerald-600" : "text-red-600"
                      }`}
                    >
                      {a.status}
                    </span>
                  )}
                  {a.message && <p className="text-xs text-slate-400">{a.message}</p>}
                </div>
                <span className="text-xs text-slate-400">{new Date(a.createdAt).toLocaleString("sr-RS")}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
